import React, {useEffect, useState} from "react";
import {IFilterTransactionRouteModel, ITransactionRouteDto} from "../../../interfaces/IUtilities";
import {getAll} from "../../../pages/_request";
import {useNavigate} from "react-router-dom";
import {useToast} from "./ToastContext";
import Loader from "./Loader";
import {useTranslation} from "react-i18next";
import Pagination from "./Pagination";

interface TransactionRoutesSearchParams {
    serviceid: string | null,
    transactionid?: string | undefined,
    isTransactionSearchOpen: boolean,
    onSelect: (sourceid: string[]) => void
    onClose: () => void
    onError: (error: unknown) => void,
    options?: Partial<{
        title: string, // The title on top of modal
        showDeleted: boolean, // Show deleted transaction routes
        multipleSelect: boolean, // Select multiple routes or not
        canSelectEmptyRoutes: boolean, // Defines if the user can select empty transaction routes (with no parameters)
    }>
}

const TransactionRoutesSearch: React.FC<TransactionRoutesSearchParams> = ({
                                     serviceid,
                                     transactionid,
                                     isTransactionSearchOpen,
                                     onSelect,
                                     onClose,
                                     onError = () => {},
                                     options
                                 }: TransactionRoutesSearchParams) => {

    const {showToast} = useToast();
    const {t} = useTranslation();

    const [transactions, setTransactions] = useState([] as ITransactionRouteDto[]);
    const [selectedArr, setSelectedArr] = useState([] as string[]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("routeLabel");
    const [asc, setAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    let order = asc ? "asc" : "desc";

    const fetchTransactionRoutes = async (filters: IFilterTransactionRouteModel = {}) => {
        try {
            const params = [
                {key: 'page', value: page},
                {key: 'pageSize', value: pageSize},
                {key: 'sortBy', value: sortField},
                {key: 'order', value: order},
                {key: 'showDeleted', value: options?.showDeleted ? 1 : 0},                
            ];
            //if (filters.id) params.push({key: 'paramType', value: filters.id});
             if (filters.id) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.id))) {
                    params.push({ key: 'id', value: filters.id });
                } else {
                    showToast(t("IdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            };
            if (filters.routeLabel) params.push({key: 'routeLabel', value: filters.routeLabel});
            if (filters.className) params.push({key: 'className', value: filters.className});
            if (filters.postLocation) params.push({key: 'postLocation', value: filters.postLocation});

            let result = await getAll(`/api/transactionroutes/getall/${serviceid}`,
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data.data as any;
            dataResults = dataResults["items"] as ITransactionRouteDto[];
            setTotal(result?.data.data.totalCount);
            setTotalPages(Math.ceil(result?.data.data.totalCount / result?.data.data.pageSize));

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactions(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            onError(error);
        }
    }

    const handleSelection = async (id: string) => {
        if (options?.multipleSelect) {
            // Check if id is already selected
            const indexSelected = selectedArr.indexOf(id);
            if (indexSelected > -1) {
                setSelectedArr(selectedArr.filter(item => item !== id));
            } else {
                setSelectedArr([...selectedArr, id]);
            }
        } else {
            onSelect([id]);
        }
    }

    const onSelectMultiple = (ids: string[]) => {
        onSelect(ids);
        setSelectedArr([]);
    }

    const closeMyself = () => {
        setSelectedArr([]);
        onClose();
    }

    const isIncludedInSelected = (id: string) => {
        return selectedArr.includes(id);
    }

    useEffect(() => {

        // Search transaction routes only if offcanvas is visible
        if (isTransactionSearchOpen) {
            fetchTransactionRoutes();
        }
    }, [isTransactionSearchOpen, navigate, showToast, page, sortField, asc, pageSize]);

    return (
        <>
            <div className={`offcanvas w-50 offcanvas-end ${isTransactionSearchOpen ? 'show' : ''}`} id="offcanvas"
                 data-bs-backdrop="static"
                 aria-labelledby="offcanvasLabel">
                <div className="offcanvas-header">
                    <h5 className="offcanvas-title" id="offcanvasLabel">{ options?.title }</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"
                            onClick={closeMyself}></button>
                </div>
                <div className="offcanvas-body">
                    {loading ? <Loader/> : (<>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                <tr>
                                    <th style={{width: "30%"}}>Label</th>
                                    <th style={{width: "30%"}}>Name</th>
                                    <th style={{width: "30%"}}>Number of Parameters</th>
                                    <th className="text-wrap" style={{width: "20%"}}>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="table-group-divider">
                                {transactions.map(transactionRoute => (
                                    <tr key={transactionRoute.id}>
                                        <td className={transactionid === transactionRoute.id ? 'text-muted':''}>{transactionRoute.routeLabel}</td>
                                        <td className={transactionid === transactionRoute.id ? 'text-muted':''}>{transactionRoute.className}</td>
                                        <td className={transactionid === transactionRoute.id ? 'text-muted':''}>{transactionRoute.totalCountTransactionParameters}</td>
                                        <td className="text-wrap text-end">
                                            {transactionid !== transactionRoute.id ?
                                                <button disabled={transactionRoute.totalCountTransactionParameters === 0} 
                                                        title={t('Copy')} 
                                                        className={`btn btn-sm me-2 ${isIncludedInSelected(transactionRoute.id) ? 'gg-btn' : 'btn-warning'}`}
                                                        type="button"
                                                        onClick={() => handleSelection(transactionRoute.id)}>
                                                            { ! isIncludedInSelected(transactionRoute.id) ?
                                                                <>{t('Select')}</> : <>{t('Remove')}</>
                                                            }
                                                </button>
                                            : <></> }
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </>)}

                    <Pagination currentPage={page}
                                            onPageChange={(page) => { setPage(page) }}
                                            pageSize={pageSize}
                                            onPageSizeChange={(size) => setPageSize(size)}
                                            totalItems={total} />

                    { options?.multipleSelect ? <div>
                        <div className="bg-light p-2 text-center">
                            <button
                                className="btn btn-info w-100"
                                onClick={() => onSelectMultiple(selectedArr)}>
                                    {t('SubmitAndCloseModal', {selectedCount:selectedArr.length})}
                        </button>
                        </div>
                    </div> : <></> }
                </div>
            </div>
        </>
    );
}

export default TransactionRoutesSearch